import { useContext, useEffect, useRef, useState } from 'react'
import useWebSocketLib, { ReadyState } from 'react-use-websocket'

import { AuthSessionContext } from '@context/AuthContext'

interface UseWebSocketOptions {
  onOpen?: () => void
  onClose?: () => void
  onError?: (event: WebSocketEventMap['error']) => void
  onMessage?: (event: MessageEvent) => void
}

interface UseWebSocketResult {
  sendMessage: (message: string) => void
  lastMessage: MessageEvent | null
  readyState: ReadyState
  connectionStatus: string
  messageHistory: MessageEvent[]
}

export const useWebSocket = (path: string, options?: UseWebSocketOptions): UseWebSocketResult => {
  const [socketUrl, setSocketUrl] = useState<string | null>(null)
  const [messageHistory, setMessageHistory] = useState<MessageEvent<any>[]>([])
  const [auth] = useContext(AuthSessionContext)
  const didUnmount = useRef(false)

  useEffect(() => {
    console.log('[WebSocket] Setting up WebSocket URL for path:', path)
    setSocketUrl(`/api/${path}/ws`)

    return () => {
      didUnmount.current = true
      console.log('[WebSocket] Cleaning up WebSocket URL')
      setSocketUrl(null)
      setMessageHistory([])
    }
  }, [auth?.accessToken, path])

  const { sendMessage, lastMessage, readyState } = useWebSocketLib(socketUrl, {
    queryParams:
      process.env.NODE_ENV === 'development'
        ? {
            token: auth?.accessToken ?? '',
          }
        : undefined,
    onOpen: () => {
      console.log('WebSocket connection opened')
      options?.onOpen?.()
    },
    onClose: () => {
      console.log('WebSocket connection closed')
      options?.onClose?.()
    },
    onError: (event: Event) => {
      console.error('WebSocket error:', event)
      options?.onError?.(event)
    },
    heartbeat: {
      message: 'ping',
      timeout: 60000,
      interval: 25000,
    },
    // We will try to reconnect for 2 minutes (120 attempts with 1 second interval)
    shouldReconnect: () => !didUnmount.current,
    reconnectAttempts: 120,
    reconnectInterval: 1000,
    onMessage: (event: MessageEvent<any>) => {
      console.log('WebSocket message received:', event)
      setMessageHistory((prev) => prev.concat(event))
      options?.onMessage?.(event)
    },
  })

  useEffect(() => {
    console.log('[WebSocket] Ready state changed:', readyState)
  }, [readyState])

  useEffect(() => {
    if (lastMessage !== null) {
      setMessageHistory((prev) => prev.concat(lastMessage))
    }
  }, [lastMessage])

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState]

  return {
    sendMessage,
    lastMessage,
    readyState,
    connectionStatus,
    messageHistory,
  }
}
